<script setup>
    import { watch, reactive, ref } from 'vue'
    import { useRouter } from 'vue-router'
    const router = useRouter()
    import { ref as databaseRef, get, update } from "firebase/database"
    import Firebase from "../../firebase_settings/index.js"
    import utilFunctions from '../../firebase_settings/utils.js'
    import { encrypt } from '../../firebase_settings/crypto.js'

    const database = Firebase.database

    const props = defineProps({
        DeviceType: {
            type: String,
            default: ''
        },
        corporateId: {
            type: String,
            default: ''
        }
    });
    const emit = defineEmits(['executeMethod']);
    const reactiveObj = reactive({
        //ドア（端末名）
        TerminalName: '',
        //SwitchBot デバイスID
        SwitchBotUid: '',
        //設置先
        Location: '',
        //親機/子機/単独
        TerminalType: '',
        //端末種別
        DeviceType: props.DeviceType
    })

    // SwitchBot認証情報設定
    const switchBotTokenSet = ref(false)
    const switchBotCreds = reactive({ Token: '', SecretKey: '' })
    const isSavingCreds = ref(false)
    const isCheckingCreds = ref(false)

    const checkSwitchBotCredentials = async () => {
        if (!props.corporateId) return
        isCheckingCreds.value = true
        try {
            const snap = await get(databaseRef(database, `CorporateId/${props.corporateId}/SwitchBot`))
            const data = snap.val() || {}
            switchBotTokenSet.value = !!(data.Token && data.SecretKey)
        } catch (e) {
            console.warn('SwitchBot credentials check error:', e)
        } finally {
            isCheckingCreds.value = false
        }
    }
    const saveSwitchBotCredentials = async () => {
        isSavingCreds.value = true
        try {
            await update(databaseRef(database, `CorporateId/${props.corporateId}/SwitchBot`), {
                Token: await encrypt(switchBotCreds.Token),
                SecretKey: await encrypt(switchBotCreds.SecretKey),
            })
            switchBotTokenSet.value = true
            switchBotCreds.Token = ''
            switchBotCreds.SecretKey = ''
            authError.value = ''

        } catch(e) {
            console.error('SwitchBot credentials save error:', e)
        } finally {
            isSavingCreds.value = false
        }
    }

    watch([() => props.DeviceType, () => props.corporateId], ([newType, newId]) => {
        if (newType === 'SwitchBot' && newId) checkSwitchBotCredentials()
    }, { immediate: true })

    // tips表示フラグ
    const tipsFlag = ref(false)
    const tipsHover = () => { tipsFlag.value = true }
    const tipsLeave = () => { tipsFlag.value = false }

    // SwitchBotデバイス一覧
    const switchBotDevices = ref([])
    const isFetchingDevices = ref(false)

    // 認証エラー種別: '' | 'not-configured' | 'invalid-credentials'
    const authError = ref('')

    // SwitchBot デバイス一覧の取得
    const fetchSwitchBotDevices = async () => {
        if (isFetchingDevices.value || switchBotDevices.value.length > 0) return
        isFetchingDevices.value = true
        try {
            const result = await utilFunctions.callFirebaseFunction('getSwitchBotDevices', { corporateId: props.corporateId })
            switchBotDevices.value = result.data.deviceList
        } catch (e) {
            console.error('SwitchBot API error:', e)
            authError.value = e.code === 'functions/not-found' ? 'not-configured' : 'invalid-credentials'
        } finally {
            isFetchingDevices.value = false
        }
    }
    // デバイス選択時にDeviceIdをセット
    const onDeviceSelect = () => {
        const selected = switchBotDevices.value.find(d => d.deviceName === reactiveObj.TerminalName)
        reactiveObj.SwitchBotUid = selected ? selected.deviceId : ''
    }
    //リアクティブオブジェクトのリセット
    const valueReset = () => {
        reactiveObj.TerminalName = ''
        reactiveObj.SwitchBotUid = ''
        reactiveObj.Location = ''
        reactiveObj.TerminalType = ''
        authError.value = ''
        switchBotCreds.Token = ''
        switchBotCreds.SecretKey = ''
        checkSwitchBotCredentials()
    }
    //モーダルウィンドウが閉じるときに実行
    const exitModal = (editFlag) => {
        reactiveObj.DeviceType = props.DeviceType
        //親コンポーネントに編集の是非とその値を渡す
        emit('executeMethod', editFlag, reactiveObj);
        //モーダルウィンドウを閉じる際に追加ボタンを非活性化
        document.getElementById('commonButtonAdd').disabled = true;
        valueReset()
    }
    //リアクティブオブジェクトの監視
    watch(reactiveObj, () => {
        //全ての値が入力された場合に追加ボタンを活性化
        if(reactiveObj.TerminalName && reactiveObj.Location && reactiveObj.TerminalType){
            document.getElementById('commonButtonAdd').disabled = false;
        }
    })
</script>

<template>
    <div class="modalArea">
        <div id="modalContent" :class="['modal', { 'modal-with-creds': props.DeviceType === 'SwitchBot' && !switchBotTokenSet && !isCheckingCreds }]">
            <div class="tooltip-wrapper">
                <p class="tooltip-content" v-show="tipsFlag">施錠までの時間は{{ props.DeviceType === 'Sesame' ? 'SESAME' : props.DeviceType }}アプリ内で設定可能です</p>
                <p class="modalTitle">端末情報の追加<img @mouseenter="tipsHover" @mouseleave="tipsLeave" src="../../assets/tips_icon.svg" class="tips"></p>
            </div>
            <div v-if="authError === 'not-configured'" class="authErrorBanner">
                SwitchBotのトークンとクライアントシークレットが設定されていません。<br>下記フォームから設定してください
            </div>
            <div v-else-if="authError === 'invalid-credentials'" class="authErrorBanner">
                SwitchBotのトークンまたはクライアントシークレットが正しくありません。<span class="settingsLink" @click="router.push('/appsettings')">アプリ設定画面</span>から再設定してください
            </div>
            <div v-if="props.DeviceType === 'SwitchBot' && !switchBotTokenSet && !isCheckingCreds" class="switchbotCredsSection">
                <p class="credsTitle">SwitchBotトークン設定</p>
                <div class="modalInfo">
                    <p>トークン</p>
                    <input class="modalTerminal switchbotTokenForm" v-model="switchBotCreds.Token" placeholder="トークン">
                </div>
                <div class="modalInfo">
                    <p>クライアントシークレット</p>
                    <input class="modalTerminal switchbotTokenForm" v-model="switchBotCreds.SecretKey" placeholder="クライアントシークレット">
                </div>
                <div class="credsButtonArea">
                    <button class="credsButton" @click="saveSwitchBotCredentials" :disabled="!switchBotCreds.Token || !switchBotCreds.SecretKey || isSavingCreds">
                        {{ isSavingCreds ? '保存中...' : '設定する' }}
                    </button>
                </div>
            </div>
            <div class="modalInfo">
                <p>端末名</p>
                <select v-if="props.DeviceType === 'SwitchBot'" class="modalTerminal" v-model="reactiveObj.TerminalName" @mousedown="fetchSwitchBotDevices" @change="onDeviceSelect">
                    <option value="" disabled>{{ isFetchingDevices ? '取得中...' : '端末名を選択' }}</option>
                    <option v-for="device in switchBotDevices" :key="device.deviceId" :value="device.deviceName">{{ device.deviceName }}</option>
                </select>
                <input v-else class="modalTerminal" v-model="reactiveObj.TerminalName">
            </div>
            <div class="modalInfo">
                <p>設置先</p>
                <select class="modalLocation" v-model="reactiveObj.Location">
                    <option value="外側">外側</option>
                    <option value="内側">内側</option>
                </select>
            </div>
            <div class="modalInfo">
                <p>親子設定</p>
                <select class="modalTerminalType" v-model="reactiveObj.TerminalType">
                    <option value="parent">親機</option>
                    <option value="single">単独</option>
                </select>
            </div>
            <button id="commonButtonAdd" @click="exitModal(true)" disabled>追加する</button>
        </div>
        <div class="modalOverlay" @click.self="exitModal(false)"></div>
    </div>
</template>

<style scoped>
    .modal {
        width: 700px;
        height: 500px;
        top: 50%;
        left: 50%;
    }
    .modal-with-creds {
        height: 1000px;
    }
    #commonButtonAdd {
        width: 190px;
        height: 70px;
        top: 12%;
        left: 50%;
    }
    .tips {
        cursor: pointer;
        vertical-align: middle;
        margin-left: 6px;
    }
    .switchbotCredsSection {
        border: 1px solid #999;
        border-radius: 8px;
        padding: 16px 16px 35px 16px;
        margin-bottom: 12px;
        background-color: #f5f5f5;
        display: flex;
        flex-direction: column;
        gap: 5%;
    }
    .switchbotCredsSection .modalInfo {
        padding: 0 4% 0 4%;
    }
    .switchbotCredsSection .modalInfo p {
        white-space: nowrap;
    }
    .credsTitle {
        font-weight: bold;
        margin-bottom: 8px;
    }
    .credsButtonArea {
        text-align: center;
        margin-top: 10px;
    }
    .credsButton {
        width: 140px;
        height: 44px;
        border-radius: 8px;
        background-color: #4a90d9;
        color: white;
        border: none;
        cursor: pointer;
        font-size: 14px;
    }
    .credsButton:disabled {
        background-color: #aaa;
        cursor: not-allowed;
    }
    .settingsLink {
        text-decoration: underline;
        cursor: pointer;
        color: inherit;
        font-size: inherit;
    }
    .authErrorBanner {
        background-color: #fdecea;
        color: #c62828;
        border: 1px solid #f44336;
        border-radius: 4px;
        padding: 10px 14px;
        margin-bottom: 12px;
        font-size: 13px;
    }
    .tooltip-wrapper {
        position: relative;
    }
    .tooltip-content {
        position: absolute;
        color: white;
        background-color: gray;
        border-radius: 0.5em;
        padding: 5px 10px;
        z-index: 10;
        top: -50px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
    }
    .tooltip-content:before {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -12px;
        border: 12px solid transparent;
        border-top: 12px solid gray;
    }
    .switchbotTokenForm{
        width: 400%;
    }
    @media (max-width: 1150px) {
        .modal {
            width: 85%;
            height: auto;
            display: flex;
            justify-content: left;
            align-items: left;
            overflow-y: scroll;
            overflow-x: hidden;
        }
        .modal p {
            padding: 10px 0 10px 0;
        }
        .modalInfo {
            flex-direction: column;
            padding: 3% 0 3% 0;
            position: relative;
        }
        .modalInfo p {
            position: relative;
            top: 0%;
            width: 100%;
            font-size: 16px;
        }
        #commonButtonAdd {
            margin: 20px 0 20px 0;
            top: 30px;
            height: auto;
        }
        .tooltip-content {
            white-space: normal;
            max-width: 75%;
            top: calc(100% + 5px);
            left: 0;
            transform: none;
        }
        .tooltip-content:before {
            top: auto;
            bottom: 100%;
            border-top: none;
            border-bottom: 12px solid gray;
        }
    }
</style>